import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
    fetchGroupClients,
    getSelectedClientId,
    setSelectedClient,
} from '../ClientSelection';
import { BRAND } from '../CompanyInfo';

/**
 * Firm picker for the COMPANY login. A rep signs in against one firm -
 * AuthRequestCust carries a single wclientid - so the firm is chosen before
 * credentials are checked.
 *
 * Selecting also stores that firm's localUrl, which the reports screen reads.
 * The customer login does not use this: it goes straight to 5811997.
 *
 * onReady(clientId) fires on every change so the parent can keep the submit
 * button disabled until a firm is chosen.
 */
const ClientSelect = ({ onReady, label = 'Firm' }) => {
    const [clients, setClients] = useState([]);
    const [selected, setSelected] = useState('');
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;

        fetchGroupClients()
            .then((list) => {
                if (cancelled) return;
                setClients(list);

                // Reuse the previous choice when it is still in the group,
                // otherwise pick the only firm if there is just one.
                const previous = getSelectedClientId();
                const match = list.find((c) => c.wClientId === previous);
                const initial = match || (list.length === 1 ? list[0] : null);

                if (initial) {
                    setSelectedClient(initial);
                    setSelected(initial.wClientId);
                    if (onReady) onReady(initial.wClientId);
                } else if (onReady) {
                    onReady('');
                }
            })
            .catch((err) => {
                console.error('Could not load the firm list', err);
                if (cancelled) return;
                setFailed(true);
                toast.error('Could not load the firm list. Please reload the page.');
                if (onReady) onReady('');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changeHandler = (e) => {
        const id = e.target.value;
        setSelected(id);
        const client = clients.find((c) => c.wClientId === id);
        if (client) {
            setSelectedClient(client);
            if (onReady) onReady(id);
        } else if (onReady) {
            onReady('');
        }
    };

    return (
        <>
            {/* was a 20px, 650-weight label with a 10px left indent and a
                <br /> - it did not match any other field on the form */}
            <label htmlFor="client">{label}</label>
            <select
                id="client"
                name="client"
                className='form-control'
                value={selected}
                onChange={changeHandler}
                disabled={loading || failed || clients.length === 0}
                style={{ borderColor: selected ? BRAND.green : undefined }}
            >
                <option value="">
                    {loading
                        ? 'Loading firms...'
                        : failed
                            ? 'Could not load firms'
                            : clients.length === 0
                                ? 'No firms available'
                                : 'Select firm'}
                </option>
                {clients.map((c) => (
                    <option key={c.wClientId} value={c.wClientId}>
                        {c.firmName}
                    </option>
                ))}
            </select>
        </>
    );
};

export default ClientSelect;
